import React, { useEffect, useMemo, useState } from 'react';
import {
  BanknotesIcon,
  PlusIcon,
  PencilSquareIcon,
  ArrowPathIcon,
  EyeIcon,
  LightBulbIcon,
} from '@heroicons/react/24/outline';
import {
  useDashboardMetaCompra,
  useMetasCompra,
} from '../hooks/useMetaCompra';
import { useEmpresas } from '../hooks/usePesquisas';
import { usePermissoes } from '../hooks/usePermissoes';
import { money } from '../utils/format';
import ModalMetaCompra from '../components/metasCompra/ModalMetaCompra';
import MetaCompraDrillDownDrawer from '../components/metasCompra/MetaCompraDrillDownDrawer';
import AjudaTitulo from '../components/ui/AjudaTitulo';
import SortHeader, { useSort } from '../components/ui/SortHeader';
import { inputIris } from '../components/ui/iris';
import type { BreakdownMetaCompra, DimensaoDrilldown, MetaCompra } from '../types/metaCompra';

// ── Helpers ────────────────────────────────────────────────────────────────

const STORAGE_EMPRESAS = 'metas.empresas';
const STORAGE_ANOMES = 'metas.anoMes';

function anoMesAtual(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

function nomeMes(anoMes: string): string {
  if (!/^\d{4}-\d{2}$/.test(anoMes)) return anoMes;
  const [ano, mes] = anoMes.split('-').map(Number);
  const nomes = ['Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
                 'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'];
  return `${nomes[mes - 1]} / ${ano}`;
}

function corPercentual(pct: number): { bg: string; text: string; bar: string; label: string } {
  if (pct >= 100) {
    return { bg: 'bg-danger-soft', text: 'text-danger', bar: 'bg-danger', label: 'Acima da meta' };
  }
  if (pct >= 80) {
    return { bg: 'bg-warning-soft', text: 'text-warning', bar: 'bg-warning', label: 'Alerta' };
  }
  return { bg: 'bg-ok-soft', text: 'text-ok', bar: 'bg-ok', label: 'Dentro da meta' };
}

/** Comparação de sets (ordem-independente). */
function setsIguais(a: string[], b: string[]): boolean {
  if (a.length !== b.length) return 0 === a.length;
  const sb = new Set(b.map((x) => x.toUpperCase()));
  return a.every((x) => sb.has(x.toUpperCase()));
}

// ── Página ────────────────────────────────────────────────────────────────

const MetasCompraPage: React.FC = () => {
  const { temPermissao, isAdmin } = usePermissoes();
  // Audit Fase B (2026-05-21): granular por ação destrutiva/efetivadora.
  // `compras.metas` libera VER a tela; `compras.meta.gravar` libera criar/editar.
  const podeEditar = isAdmin || temPermissao('compras.meta.gravar');

  // Filtros persistidos
  const [empresasSel, setEmpresasSel] = useState<string[]>(() => {
    try {
      const raw = localStorage.getItem(STORAGE_EMPRESAS);
      return raw ? (JSON.parse(raw) as string[]) : [];
    } catch { return []; }
  });
  const [anoMes, setAnoMes] = useState<string>(() => {
    return localStorage.getItem(STORAGE_ANOMES) ?? anoMesAtual();
  });

  useEffect(() => { localStorage.setItem(STORAGE_EMPRESAS, JSON.stringify(empresasSel)); }, [empresasSel]);
  useEffect(() => { localStorage.setItem(STORAGE_ANOMES, anoMes); }, [anoMes]);

  // Aba de breakdown e drill-down
  const [abaBreakdown, setAbaBreakdown] = useState<DimensaoDrilldown>('fornecedor');
  const [drilldownAlvo, setDrilldownAlvo] = useState<{ dimensao: DimensaoDrilldown; codigo: string; titulo: string } | null>(null);

  // Modal de criar/editar
  const [modalAberta, setModalAberta] = useState(false);
  const [metaEditando, setMetaEditando] = useState<MetaCompra | null>(null);

  // Dados
  const { data: empresas = [] } = useEmpresas();
  const nomeEmpresa = useMemo(() => {
    const map = new Map<string, string>();
    empresas.forEach((e) => map.set(e.codigo, e.nomeFantasia || e.razaosocial || e.codigo));
    return map;
  }, [empresas]);

  // Auto-seleção quando empresas chegam: se vazio, seleciona a primeira (UX)
  useEffect(() => {
    if (empresasSel.length === 0 && empresas.length > 0) {
      setEmpresasSel([empresas[0].codigo]);
    }
  }, [empresas, empresasSel.length]);

  const { data: dashboard, isLoading: isLoadingDash, isFetching: isFetchingDash, refetch: refetchDash } =
    useDashboardMetaCompra(anoMes, empresasSel, empresasSel.length > 0);
  const { data: metas = [] } = useMetasCompra({ anoMes });

  const toggleEmpresa = (cod: string) => {
    setEmpresasSel((prev) => prev.includes(cod) ? prev.filter((e) => e !== cod) : [...prev, cod]);
  };

  const abrirCadastro = (metaExistente?: MetaCompra) => {
    setMetaEditando(metaExistente ?? null);
    setModalAberta(true);
  };
  const fecharModal = () => {
    setModalAberta(false);
    setMetaEditando(null);
  };

  // Quando o usuário clica num chip de sugestão: ajusta o filtro pra bater
  // com o set da meta sugerida (match exato vai pegar).
  const aplicarSugestaoMeta = (m: MetaCompra) => {
    setEmpresasSel(m.empresas);
  };

  const breakdownAtual: BreakdownMetaCompra[] = useMemo(() => {
    if (!dashboard) return [];
    return abaBreakdown === 'fornecedor' ? dashboard.porFornecedor
      : abaBreakdown === 'marca' ? dashboard.porMarca
      : dashboard.porGrupo;
  }, [dashboard, abaBreakdown]);

  const totalBreakdown = breakdownAtual.reduce((acc, b) => acc + b.valor, 0);
  const cores = corPercentual(dashboard?.percentualAtingido ?? 0);

  // Ordenação client-side do breakdown (dado completo, não paginado).
  const { sort: sortBd, onSort: onSortBd } = useSort();
  const breakdownOrdenado = useMemo(() => {
    if (!sortBd) return breakdownAtual;
    const dir = sortBd.desc ? -1 : 1;
    return [...breakdownAtual].sort((a, b) => {
      switch (sortBd.campo) {
        case 'nome': return (a.nome ?? a.codigo).localeCompare(b.nome ?? b.codigo) * dir;
        case 'pedido': return (a.valorPedido - b.valorPedido) * dir;
        case 'cancelado': return (a.valorCancelado - b.valorCancelado) * dir;
        case 'saldo': return (a.valor - b.valor) * dir;
        case 'pedidos': return (a.qtdePedidos - b.qtdePedidos) * dir;
        default: return 0;
      }
    });
  }, [breakdownAtual, sortBd]);

  // Match exato encontrado pelo backend? (descricaoMeta != null)
  const temMetaMatch = !!dashboard?.descricaoMeta;
  const metaMatchOriginal: MetaCompra | undefined = temMetaMatch && dashboard
    ? dashboard.metasDoMes.find((m) => m.id === dashboard.metaId)
    : undefined;

  return (
    <div className="mx-auto flex h-full w-full max-w-[1280px] flex-col gap-3">
      {/* Page header */}
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <h1 className="flex items-center gap-2 text-[22px] font-bold text-tx tracking-[-0.02em]">
            <BanknotesIcon className="h-6 w-6 flex-shrink-0 text-accent-text" aria-hidden />
            Metas de Compra
            <AjudaTitulo>
              Planejamento mensal <b>vs</b> realizado por meta — uma meta pode cobrir 1 ou N empresas.
              Use o filtro de <b>mês</b> e selecione as <b>empresas</b> na barra abaixo; cadastre ou
              edite metas pelo botão à direita.
            </AjudaTitulo>
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={() => refetchDash()}
            disabled={isFetchingDash}
            className="flex h-8 items-center gap-1 rounded-iris border border-line px-2.5 text-xs text-tx-2 hover:bg-surface-2 transition-colors disabled:opacity-50"
          >
            <ArrowPathIcon className={`h-3.5 w-3.5 ${isFetchingDash ? 'animate-spin' : ''}`} />
            Atualizar
          </button>
          {podeEditar && (
            <button
              type="button"
              onClick={() => abrirCadastro(metaMatchOriginal)}
              className="flex h-8 items-center gap-1 rounded-iris bg-accent px-3 text-xs font-medium text-white hover:opacity-90 transition-opacity"
            >
              {metaMatchOriginal ? <PencilSquareIcon className="h-3.5 w-3.5" /> : <PlusIcon className="h-3.5 w-3.5" />}
              {metaMatchOriginal ? `Editar "${metaMatchOriginal.descricao}"` : 'Cadastrar meta'}
            </button>
          )}
        </div>
      </div>

      {/* Empty state: filtro sem match mas há metas cadastradas — sugestões clicáveis */}
      {dashboard && !temMetaMatch && dashboard.metasDoMes.length > 0 && empresasSel.length > 0 && (
        <div className="rounded-iris-lg border border-warning-border bg-warning-soft p-3">
          <div className="flex items-start gap-2">
            <LightBulbIcon className="mt-0.5 h-4 w-4 shrink-0 text-warning" />
            <div className="flex-1">
              <p className="text-xs font-medium text-tx">
                Não existe meta cadastrada pra exatamente este conjunto de empresas.
              </p>
              <p className="mt-0.5 text-[11px] text-warning">
                Clique em uma das metas abaixo pra ajustar o filtro:
              </p>
              <div className="mt-2 flex flex-wrap gap-1.5">
                {dashboard.metasDoMes.map((m) => {
                  const matchAtual = setsIguais(m.empresas, empresasSel);
                  return (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => aplicarSugestaoMeta(m)}
                      disabled={matchAtual}
                      className="rounded-iris-sm border border-warning-border bg-surface px-2 py-1 text-[11px] text-tx hover:bg-warning-soft disabled:opacity-50"
                      title={m.empresas.join(', ')}
                    >
                      <span className="font-medium">{m.descricao}</span>
                      <span className="ml-1 text-warning">({m.empresas.length}) {money(m.valorMeta)}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            {podeEditar && (
              <button
                type="button"
                onClick={() => abrirCadastro()}
                className="rounded-iris-sm bg-warning px-2 py-1 text-[11px] font-medium text-white hover:opacity-90 transition-opacity"
              >
                Cadastrar nova
              </button>
            )}
          </div>
        </div>
      )}

      {/* KPIs hero */}
      {empresasSel.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-iris-lg border border-dashed border-line-strong bg-surface-2 p-10 text-center">
          <BanknotesIcon className="h-10 w-10 text-tx-3" />
          <p className="text-sm text-tx-2">Selecione ao menos uma empresa pra ver o dashboard.</p>
        </div>
      ) : isLoadingDash ? (
        <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => (
            <div key={i} className="h-24 animate-pulse rounded-iris-lg bg-surface-2" />
          ))}
        </div>
      ) : dashboard ? (
        <div className="grid grid-cols-2 gap-2 sm:gap-3 lg:grid-cols-4">
          {/* Meta */}
          <div className="rounded-iris-lg border border-line border-l-4 border-l-accent bg-surface p-3 shadow-sm">
            <div className="text-[10px] uppercase tracking-wide text-tx-3">
              Meta · {nomeMes(anoMes)}
            </div>
            <div className="mt-1 text-xl font-bold text-tx">
              {temMetaMatch ? money(dashboard.valorMeta) : <span className="text-tx-3">—</span>}
            </div>
            <div className="mt-1 text-[10px] text-accent-text">
              {temMetaMatch
                ? <>{dashboard.descricaoMeta} · {dashboard.empresas.length} empresa{dashboard.empresas.length === 1 ? '' : 's'}</>
                : 'Sem meta consolidada pra este filtro'}
            </div>
          </div>

          {/* Comprado — saldo líquido + breakdown bruto/cancelado */}
          <div className="rounded-iris-lg border border-line border-l-4 border-l-line-strong bg-surface p-3 shadow-sm">
            <div className="text-[10px] uppercase tracking-wide text-tx-3">Saldo (líquido)</div>
            <div className="mt-1 text-xl font-bold text-tx">{money(dashboard.valorRealizado)}</div>
            <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-tx-3">
              <span>Pedido: <span className="font-medium text-tx-2">{money(dashboard.valorPedido)}</span></span>
              {dashboard.valorCancelado > 0 && (
                <span>Cancelado: <span className="font-medium text-danger">−{money(dashboard.valorCancelado)}</span></span>
              )}
              <span>{dashboard.qtdePedidos} pedidos</span>
            </div>
          </div>

          {/* Saldo */}
          <div className={`rounded-iris-lg border border-line border-l-4 bg-surface p-3 shadow-sm ${
            !temMetaMatch ? 'border-l-line-strong'
              : dashboard.saldo >= 0 ? 'border-l-ok'
              : 'border-l-danger'
          }`}>
            <div className={`text-[10px] uppercase tracking-wide ${
              !temMetaMatch ? 'text-tx-3'
                : dashboard.saldo >= 0 ? 'text-ok'
                : 'text-danger'
            }`}>
              Saldo restante
            </div>
            <div className="mt-1 text-xl font-bold text-tx">
              {temMetaMatch ? money(dashboard.saldo) : <span className="text-tx-3">—</span>}
            </div>
            <div className={`mt-1 text-[10px] ${
              !temMetaMatch ? 'text-tx-3'
                : dashboard.saldo >= 0 ? 'text-ok'
                : 'text-danger'
            }`}>
              {!temMetaMatch ? '—' : dashboard.saldo >= 0 ? 'Disponível pra novos pedidos' : 'Acima da meta — atenção'}
            </div>
          </div>

          {/* % atingido — barra de progresso colorida carrega o status; card no padrão neutro */}
          <div className="rounded-iris-lg border border-line border-l-4 border-l-line bg-surface p-3 shadow-sm">
            <div className={`text-[10px] uppercase tracking-wide ${temMetaMatch ? cores.text : 'text-tx-3'}`}>% atingido</div>
            <div className="mt-1 flex items-baseline gap-1">
              <span className="text-xl font-bold text-tx">
                {temMetaMatch ? dashboard.percentualAtingido.toFixed(1) : '—'}
              </span>
              {temMetaMatch && <span className="text-xs text-tx-3">%</span>}
            </div>
            {temMetaMatch && (
              <div className="mt-2 h-1.5 w-full overflow-hidden rounded-full bg-surface-2">
                <div
                  className={`h-full rounded-full ${cores.bar}`}
                  style={{ width: `${Math.min(dashboard.percentualAtingido, 100)}%` }}
                />
              </div>
            )}
            <div className={`mt-1 text-[10px] ${temMetaMatch ? cores.text : 'text-tx-3'}`}>
              {temMetaMatch ? cores.label : 'Cadastre meta pra ver progresso'}
            </div>
          </div>
        </div>
      ) : null}

      {/* Toolbar de filtros */}
      <div className="flex flex-wrap items-end gap-3 rounded-iris-lg border border-line bg-surface p-3 shadow-sm">
        <div className="flex flex-col">
          <label className="mb-1 text-[10px] uppercase tracking-wide text-tx-3">Mês</label>
          <input
            type="month"
            value={anoMes}
            onChange={(e) => setAnoMes(e.target.value)}
            className={`${inputIris} h-8 text-sm`}
          />
        </div>
        <div className="flex-1 min-w-[240px]">
          <label className="mb-1 block text-[10px] uppercase tracking-wide text-tx-3">Empresas</label>
          <div className="flex flex-wrap gap-1">
            {empresas.map((e) => {
              const ativa = empresasSel.includes(e.codigo);
              return (
                <button
                  key={e.codigo}
                  type="button"
                  onClick={() => toggleEmpresa(e.codigo)}
                  className={`rounded-iris-sm border px-2 py-1 text-[11px] transition-colors ${
                    ativa
                      ? 'border-accent bg-accent-soft text-accent-text'
                      : 'border-line bg-surface text-tx-2 hover:bg-surface-2'
                  }`}
                  title={e.razaosocial}
                >
                  <span className="font-mono">{e.codigo}</span>
                  <span className="ml-1">{e.nomeFantasia || e.razaosocial}</span>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Tabs breakdown + tabela */}
      {dashboard && empresasSel.length > 0 && (
        <div className="flex flex-1 flex-col gap-2 overflow-hidden rounded-iris-lg border border-line bg-surface shadow-sm">
          <div className="flex items-center gap-1 border-b border-line px-2 pt-2">
            {(['fornecedor', 'marca', 'grupo'] as DimensaoDrilldown[]).map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setAbaBreakdown(d)}
                className={`rounded-t-md px-3 py-1.5 text-xs font-medium transition-colors ${
                  abaBreakdown === d
                    ? 'bg-surface-2 text-tx'
                    : 'text-tx-3 hover:text-tx-2'
                }`}
              >
                Por {d === 'fornecedor' ? 'fornecedor' : d}
              </button>
            ))}
          </div>

          <div className="flex-1 overflow-auto">
            {breakdownAtual.length === 0 ? (
              <p className="p-6 text-center text-sm text-tx-3">Nenhum pedido neste período/dimensão.</p>
            ) : (
              <table className="w-full text-xs">
                <thead className="sticky top-0 z-10 bg-surface-2">
                  <tr className="border-b border-line text-left text-tx-2">
                    <SortHeader campo="nome" label={abaBreakdown === 'fornecedor' ? 'Fornecedor' : abaBreakdown === 'marca' ? 'Marca' : 'Grupo'} sort={sortBd} onSort={onSortBd} />
                    <SortHeader campo="pedido" label="Pedido" sort={sortBd} onSort={onSortBd} alinhar="right" />
                    <SortHeader campo="cancelado" label="Cancelado" sort={sortBd} onSort={onSortBd} alinhar="right" />
                    <SortHeader campo="saldo" label="Saldo" sort={sortBd} onSort={onSortBd} alinhar="right" />
                    <th className="px-3 py-2 text-right font-medium">% do total</th>
                    <SortHeader campo="pedidos" label="Pedidos" sort={sortBd} onSort={onSortBd} alinhar="right" />
                    <th className="px-3 py-2"></th>
                  </tr>
                </thead>
                <tbody>
                  {breakdownOrdenado.map((b) => {
                    const pct = totalBreakdown > 0 ? (b.valor / totalBreakdown) * 100 : 0;
                    return (
                      <tr key={b.codigo} className="border-b border-line last:border-0 hover:bg-surface-2">
                        <td className="px-3 py-2">
                          <span className="font-mono text-[10px] text-tx-3">{b.codigo}</span>
                          <span className="ml-1 text-tx">{b.nome || <span className="text-tx-3">sem nome</span>}</span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right text-tx-2">
                          {money(b.valorPedido)}
                        </td>
                        <td className={`whitespace-nowrap px-3 py-2 text-right ${b.valorCancelado > 0 ? 'text-danger' : 'text-tx-3'}`}>
                          {b.valorCancelado > 0 ? `−${money(b.valorCancelado)}` : '—'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right font-semibold text-tx">
                          {money(b.valor)}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-2">
                            <div className="h-1 w-20 overflow-hidden rounded-full bg-surface-2">
                              <div className="h-full rounded-full bg-accent" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-tx-2">{pct.toFixed(1)}%</span>
                          </div>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right text-tx-2">
                          {b.qtdePedidos}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right">
                          <button
                            type="button"
                            onClick={() => setDrilldownAlvo({ dimensao: abaBreakdown, codigo: b.codigo, titulo: b.nome || b.codigo })}
                            className="inline-flex items-center gap-1 rounded-iris border border-line bg-surface px-2 py-0.5 text-[11px] text-tx-2 hover:bg-surface-2 transition-colors"
                          >
                            <EyeIcon className="h-3 w-3" /> Ver pedidos
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* Lista de metas cadastradas no mês */}
      {metas.length > 0 && (
        <details className="rounded-iris-lg border border-line bg-surface shadow-sm">
          <summary className="cursor-pointer select-none px-3 py-2 text-xs font-medium text-tx-2">
            Metas cadastradas em {nomeMes(anoMes)} ({metas.length})
          </summary>
          <table className="w-full text-xs">
            <thead className="bg-surface-2">
              <tr className="border-t border-line text-left text-tx-2">
                <th className="px-3 py-2 font-medium">Descrição</th>
                <th className="px-3 py-2 font-medium">Empresas</th>
                <th className="px-3 py-2 text-right font-medium">Valor</th>
                <th className="px-3 py-2 font-medium">Observação</th>
                <th className="px-3 py-2 font-medium">Criada por</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {metas.map((m) => (
                <tr key={m.id} className="border-t border-line last:border-0 hover:bg-surface-2">
                  <td className="px-3 py-1.5 font-medium text-tx">{m.descricao}</td>
                  <td className="px-3 py-1.5">
                    <div className="flex flex-wrap gap-1">
                      {m.empresas.map((cod) => (
                        <span
                          key={cod}
                          className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-tx-2"
                          title={nomeEmpresa.get(cod) ?? ''}
                        >
                          <span className="font-mono">{cod}</span>
                        </span>
                      ))}
                    </div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-right font-medium text-tx">{money(m.valorMeta)}</td>
                  <td className="px-3 py-1.5 text-tx-2">{m.observacao || <span className="text-tx-3">—</span>}</td>
                  <td className="px-3 py-1.5 text-[10px] text-tx-3">{m.criadoPor || '—'}</td>
                  <td className="px-3 py-1.5 text-right">
                    {podeEditar && (
                      <button
                        type="button"
                        onClick={() => abrirCadastro(m)}
                        className="inline-flex items-center gap-1 rounded-iris border border-line bg-surface px-2 py-0.5 text-[11px] text-tx-2 hover:bg-surface-2 transition-colors"
                      >
                        <PencilSquareIcon className="h-3 w-3" /> Editar
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}

      {modalAberta && (
        <ModalMetaCompra
          meta={metaEditando}
          defaultAnoMes={anoMes}
          defaultEmpresas={empresasSel}
          onClose={fecharModal}
        />
      )}

      {drilldownAlvo && (
        <MetaCompraDrillDownDrawer
          anoMes={anoMes}
          empresas={empresasSel}
          dimensao={drilldownAlvo.dimensao}
          codigo={drilldownAlvo.codigo}
          titulo={drilldownAlvo.titulo}
          onClose={() => setDrilldownAlvo(null)}
        />
      )}
    </div>
  );
};

export default MetasCompraPage;
